import InvoiceWorkflowRejectModal from 'components/modals/invoices/InvoiceWorkflowRejectFormModal';
import { useModal } from 'components/modals/useModal';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useInvoiceFlow } from 'pages/invoices/hooks/useInvoiceFlow';
import { HandleInvoiceTransitionArgs } from 'pages/invoices/InvoiceWorkflow';
import { useCallback } from 'react';
import { useSelector } from 'store';
import { InvoiceMatterSummary } from 'types/invoice-details';
import { FeatureFlag } from 'types/features';
import {
    InvoiceData,
    InvoiceMatterDetailsDto,
    InvoiceStatusType,
    InvoiceTransactionActionType,
    InvoiceType,
} from 'types/invoices';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

interface Args {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
    summaryData?: InvoiceMatterSummary;
    isInViewMode: boolean;
    handleStatusChange: (args: HandleInvoiceTransitionArgs) => Promise<void>;
}

/** A joint debit invoice reviewed and approved case by case, until the invoice itself is approved. */
export const useIsMultiApprovalFlow = (invoice: InvoiceData) => {
    const { isDebit, isApproved } = useInvoice(invoice);
    const isEnabled = useIsFeatureEnabled(FeatureFlag.MULTI_REVIEW_APPROVE_JOINT_INVOICE);
    return isEnabled && (invoice.invoiceMatters?.length ?? 0) > 1 && isDebit && !isApproved;
};

/**
 * A case's own rules on its invoice: whether it still edits, its review or approval on a joint invoice approved case
 * by case, and whether its changed fee arrangement may be applied.
 */
export const useCaseApproval = ({ invoice, invoiceMatter, summaryData, isInViewMode, handleStatusChange }: Args) => {
    const { openModal, closeModal } = useModal();
    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);

    const openRejectModal = useCallback((): void => {
        return openModal(
            <InvoiceWorkflowRejectModal
                invoice={invoice}
                onClose={closeModal}
                onReject={(reason) =>
                    handleStatusChange({
                        to: InvoiceStatusType.DRAFT,
                        transactionActionType: InvoiceTransactionActionType.REJECTED,
                        comment: reason,
                    })
                }
                rejectToState={InvoiceStatusType.DRAFT}
                isRequestedToPostpone={false}
            />,
            closeModal
        );
    }, [openModal, invoice, closeModal, handleStatusChange]);

    const { isInvoiceRejected, isRequestedToPostpone } = useInvoiceFlow(invoice);
    const { isDebit } = useInvoice(invoice);
    const isSplitInvoice = invoice.isSplit ?? false;
    const isInvoiceDraft = invoice.invoiceStatusTypeCode === InvoiceStatusType.DRAFT;
    const isInvoiceReviewed = invoice.invoiceStatusTypeCode === InvoiceStatusType.REVIEWED;
    const isMatterStatusUpdatable = summaryData?.isMatterStatusUpdatable && !isRequestedToPostpone;
    const isInvoiceMatterReviewed = !!invoiceMatter.reviewedAt;
    const isInvoiceMatterApproved = !!invoiceMatter.approvedAt;
    const isRejected =
        isInvoiceRejected &&
        !!invoice.invoiceTransitions?.length &&
        invoice.invoiceTransitions[0].matter?.id === invoiceMatter.matterId;

    const isMultiApprovalWorkflowActive = useIsMultiApprovalFlow(invoice);
    const isUserPrivilegedToUpdateApproved = useHasPermissions(['UPDATE.INVOICES.STATE_APPROVED']);
    const isInvoiceMatterReadOnly =
        isInViewMode || (isMultiApprovalWorkflowActive && isInvoiceMatterApproved && !isUserPrivilegedToUpdateApproved);

    const isMatterFeeArrangementTypeUpdateable = isDebit && isInvoiceDraft && !isInvoiceMatterReviewed;
    const hasMatterCapError =
        invoice.invoiceTypeCode === InvoiceType.DEBIT &&
        invoice.invoiceStatusTypeCode !== InvoiceStatusType.DRAFT &&
        !isSplitInvoice &&
        invoiceMatter.hasMatterCapError;

    return {
        buttonsDisabled,
        openRejectModal,
        isInvoiceDraft,
        isInvoiceReviewed,
        isMatterStatusUpdatable,
        isInvoiceMatterReviewed,
        isInvoiceMatterApproved,
        isRejected,
        isMultiApprovalWorkflowActive,
        isInvoiceMatterReadOnly,
        isMatterFeeArrangementTypeUpdateable,
        /** Shown in the case: the fee arrangement changed and may be applied here. */
        showsFeeArrangementChange:
            isMatterFeeArrangementTypeUpdateable && !isInViewMode && !!summaryData?.hasMatterFeeArrangementTypeChanged,
        hasMatterCapError,
        postponeReason: isRequestedToPostpone ? t('invoices.actions.disabled-requested-to-postpone') : undefined,
    };
};
